import { useState, type FormEvent } from "react"
import { useLocation, useNavigate } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { cacheSession } from "@client/lib/session"

export function LoginPage() {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const [email, setEmail] = useState("")
  const [code, setCode] = useState("")
  const [step, setStep] = useState<"request" | "verify">("request")
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const request = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const res = await client.api.admin.auth.request.$post({ json: { email } })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      setStep("verify")
    } catch {
      setError("Could not send the code. Try again.")
    } finally {
      setSubmitting(false)
    }
  }

  const verify = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const res = await client.api.admin.auth.verify.$post({ json: { email, code } })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      cacheSession(email.trim().toLowerCase())
      navigate("/admin", { replace: true })
    } catch {
      setError("Could not verify the code. Try again.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div key={`${pathname}${search}`} className="animate-fade-in-up min-h-screen bg-cream-50 text-ink">
      <main className="mx-auto grid min-h-screen w-full max-w-[1440px] items-center gap-12 px-5 py-10 sm:px-10 lg:grid-cols-[minmax(0,1fr)_576px] lg:gap-20 lg:px-20 lg:py-16">
        <section className="max-w-[610px]">
          <div className="inline-flex items-center gap-2.5 rounded-full border border-ink/10 bg-cream-200 px-3 py-1.5 text-sm font-medium uppercase tracking-wide text-forest-700">
            <span className="h-1.5 w-1.5 rounded-full bg-forest-700" aria-hidden />
            Admin area
          </div>
          <h1 className="mt-7 max-w-[520px] font-display text-[42px] font-bold leading-[1.05] text-ink sm:text-[56px] lg:text-[64px]">
            Keep the work <span className="text-forest-700">moving.</span>
          </h1>
          <p className="mt-5 max-w-[560px] text-base leading-7 text-ink/75 sm:text-lg">
            Sign in to manage scholarships, support partner schools, and keep every Redempta story moving forward.
          </p>
          <ul className="mt-8 space-y-3 list-disc pl-5 text-sm leading-6 text-ink/65 marker:text-forest-700 sm:text-base">
            <li>Review children, nominations, and school partnerships</li>
            <li>Track sponsorships, programs, and giving</li>
            <li>Keep the foundation&apos;s work connected</li>
          </ul>
        </section>

        <section className="rounded-[30px] border border-gold-500 bg-white p-6 sm:p-8 lg:p-10">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center overflow-hidden rounded-full bg-forest-950">
              <img src="/images/home/logo.png" alt="" className="h-full w-full object-contain p-1.5" />
            </span>
            <div>
              <p className="font-display text-2xl font-bold leading-none text-ink">Redempta</p>
              <p className="mt-1 text-sm text-ink/55">Foundation workspace</p>
            </div>
          </div>

          <div className="mt-10">
            <p className="text-sm font-semibold uppercase tracking-wide text-forest-700">{step === "request" ? "Welcome back" : "Check your inbox"}</p>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-ink sm:text-4xl">
              {step === "request" ? "Sign in to the admin workspace" : "Enter your access code"}
            </h2>
            <p className="mt-3 text-sm leading-6 text-ink/65 sm:text-base">
              {step === "request"
                ? "Enter your admin email and we'll send you a one-time code."
                : `Enter the 6-digit code sent to ${email}.`}
            </p>
          </div>

          {step === "request" ? (
            <form onSubmit={request} className="mt-8 space-y-5">
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-ink">Admin email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@redempta.org"
                  required
                  autoFocus
                  className="h-12 w-full rounded-full border border-gold-500 bg-cream-200/40 px-5 text-sm text-ink outline-none transition-colors placeholder:text-ink/40 focus:border-forest-700 focus:ring-2 focus:ring-forest-700/10"
                />
              </label>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="h-12 w-full rounded-full bg-forest-950 px-5 text-base font-semibold text-white transition-colors hover:bg-forest-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send code"}
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="mt-8 space-y-5">
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-ink">6-digit code</span>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  inputMode="numeric"
                  required
                  autoFocus
                  className="h-12 w-full rounded-full border border-gold-500 bg-cream-200/40 px-5 text-sm tracking-[0.35em] text-ink outline-none transition-colors placeholder:text-ink/40 focus:border-forest-700 focus:ring-2 focus:ring-forest-700/10"
                />
              </label>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="h-12 w-full rounded-full bg-forest-950 px-5 text-base font-semibold text-white transition-colors hover:bg-forest-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Verifying…" : "Verify code"}
              </button>
              <button
                type="button"
                onClick={() => setStep("request")}
                className="w-full rounded-full px-3 py-2 text-center text-sm font-medium text-forest-700 transition-colors hover:bg-cream-200"
              >
                Use a different email
              </button>
            </form>
          )}
        </section>
      </main>
    </div>
  )
}
